/* @jsx h */
import {
  defineComponent,
  h,
  Teleport,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useNavStack } from "./navStack";

export * from "./navStack";

export type FullScreenSheetAnimation = "slide-up" | "slide-right" | "fade";

export type FullScreenSheetProps = {
  isOpen: boolean;
  onClose?: () => void;
  /**
   * Veto a user-initiated dismissal. Awaited BEFORE the exit animation starts,
   * so returning false leaves the sheet exactly as it was.
   *
   * `onClose` fires only after the sheet has already unmounted itself, which
   * makes it useless as a guard: a consumer that checks there ("this checkout
   * has collected money, stay put") finds the screen is gone and cannot bring
   * it back, because `isOpen` never changed and so the reopen watcher has
   * nothing to react to. Guarding has to happen here instead.
   *
   * Only consulted for dismissals the sheet itself initiates (the slot's
   * `close()`, the nav-stack back gesture). A parent driving `isOpen` to false
   * has already decided, and is not second-guessed.
   */
  canClose?: () => boolean | Promise<boolean>;
  class?: string;
  style?: string;
  zIndex?: string;
  animation?: FullScreenSheetAnimation;
};

export type FullScreenSheetSlotProps = {
  close: () => void;
};

type AnimationState = "idle" | "enter" | "exit";
type RootAnimationState = "enter" | "open" | "exit" | null;

const MOBILE_ANIMATION_QUERY = "(max-width: 767px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const ENTER_DURATION_MS = 420;
const EXIT_DURATION_MS = 200;
// Native-style motion: enter responds immediately and settles gently; exit
// accelerates away so dismissal never feels sluggish.
const ENTER_EASING = "cubic-bezier(0.16, 1, 0.3, 1)";
const EXIT_EASING = "cubic-bezier(0.4, 0, 1, 1)";
const DEFAULT_ANIMATION: FullScreenSheetAnimation = "slide-right";
const STYLE_ELEMENT_ID = "lemmon-full-screen-sheet-styles";
const ROOT_ANIMATION_CLASSES = [
  "lmn-fss-root--enter",
  "lmn-fss-root--open",
  "lmn-fss-root--exit",
];

const fullScreenSheetStyles = `
@media ${MOBILE_ANIMATION_QUERY} and (prefers-reduced-motion: no-preference) {
  .lmn-fullscreen-sheet__panel[data-fss-state="enter"],
  .lmn-fullscreen-sheet__panel[data-fss-state="exit"] {
    backface-visibility: hidden;
    will-change: transform;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="slide-up"][data-fss-state="enter"] {
    animation: lmn-fss-sheet-slide-up-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="slide-up"][data-fss-state="exit"] {
    animation: lmn-fss-sheet-slide-up-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="slide-right"][data-fss-state="enter"] {
    animation: lmn-fss-sheet-slide-right-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="slide-right"][data-fss-state="exit"] {
    animation: lmn-fss-sheet-slide-right-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="fade"][data-fss-state="enter"] {
    animation: lmn-fss-sheet-fade-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .lmn-fullscreen-sheet__panel[data-fss-animation="fade"][data-fss-state="exit"] {
    animation: lmn-fss-sheet-fade-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--enter,
  .root-lemmon-app.lmn-fss-root--exit {
    backface-visibility: hidden;
    transform-origin: center;
    will-change: transform;
  }

  .root-lemmon-app.lmn-fss-root--open[data-fss-animation="slide-up"] {
    transform: translate3d(0, -3%, 0) scale(0.985);
  }

  .root-lemmon-app.lmn-fss-root--open[data-fss-animation="slide-right"] {
    transform: translate3d(-8%, 0, 0) scale(0.985);
  }

  .root-lemmon-app.lmn-fss-root--open[data-fss-animation="fade"] {
    opacity: 0.92;
    transform: scale(0.985);
  }

  .root-lemmon-app.lmn-fss-root--enter[data-fss-animation="slide-up"] {
    animation: lmn-fss-root-slide-up-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--exit[data-fss-animation="slide-up"] {
    animation: lmn-fss-root-slide-up-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--enter[data-fss-animation="slide-right"] {
    animation: lmn-fss-root-slide-right-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--exit[data-fss-animation="slide-right"] {
    animation: lmn-fss-root-slide-right-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--enter[data-fss-animation="fade"] {
    animation: lmn-fss-root-fade-in ${ENTER_DURATION_MS}ms ${ENTER_EASING} both;
  }

  .root-lemmon-app.lmn-fss-root--exit[data-fss-animation="fade"] {
    animation: lmn-fss-root-fade-out ${EXIT_DURATION_MS}ms ${EXIT_EASING} both;
  }

  @keyframes lmn-fss-sheet-slide-up-in {
    from { transform: translate3d(0, 100%, 0); }
    to { transform: translate3d(0, 0, 0); }
  }

  @keyframes lmn-fss-sheet-slide-up-out {
    from { transform: translate3d(0, 0, 0); }
    to { transform: translate3d(0, 100%, 0); }
  }

  @keyframes lmn-fss-sheet-slide-right-in {
    from { transform: translate3d(100%, 0, 0); }
    to { transform: translate3d(0, 0, 0); }
  }

  @keyframes lmn-fss-sheet-slide-right-out {
    from { transform: translate3d(0, 0, 0); }
    to { transform: translate3d(100%, 0, 0); }
  }

  @keyframes lmn-fss-sheet-fade-in {
    from { opacity: 0; transform: scale(0.985); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes lmn-fss-sheet-fade-out {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.985); }
  }

  @keyframes lmn-fss-root-slide-up-in {
    from { transform: translate3d(0, 0, 0) scale(1); }
    to { transform: translate3d(0, -3%, 0) scale(0.985); }
  }

  @keyframes lmn-fss-root-slide-up-out {
    from { transform: translate3d(0, -3%, 0) scale(0.985); }
    to { transform: translate3d(0, 0, 0) scale(1); }
  }

  @keyframes lmn-fss-root-slide-right-in {
    from { transform: translate3d(0, 0, 0); }
    to { transform: translate3d(-8%, 0, 0) scale(0.985); }
  }

  @keyframes lmn-fss-root-slide-right-out {
    from { transform: translate3d(-8%, 0, 0) scale(0.985); }
    to { transform: translate3d(0, 0, 0); }
  }

  @keyframes lmn-fss-root-fade-in {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0.92; transform: scale(0.985); }
  }

  @keyframes lmn-fss-root-fade-out {
    from { opacity: 0.92; transform: scale(0.985); }
    to { opacity: 1; transform: scale(1); }
  }
}

@media (min-width: 768px), (prefers-reduced-motion: reduce) {
  .lmn-fullscreen-sheet__panel,
  .root-lemmon-app.lmn-fss-root--enter,
  .root-lemmon-app.lmn-fss-root--open,
  .root-lemmon-app.lmn-fss-root--exit {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
`;

const isBrowser = () => typeof window !== "undefined";

const shouldAnimateOnMobile = () =>
  isBrowser() &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_ANIMATION_QUERY).matches &&
  !window.matchMedia(REDUCED_MOTION_QUERY).matches;

const ensureStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = fullScreenSheetStyles;
  document.head.appendChild(style);
};

const getAppRoot = () =>
  typeof document === "undefined"
    ? null
    : document.querySelector<HTMLElement>(".root-lemmon-app");

export const FullScreenSheet = defineComponent<FullScreenSheetProps>({
  name: "FullScreenSheet",
  props: [
    "isOpen",
    "onClose",
    "canClose",
    "class",
    "style",
    "zIndex",
    "animation",
  ],
  setup(props, { slots }) {
    const navStack = useNavStack();
    const shouldAnimateInitialOpen = props.isOpen && shouldAnimateOnMobile();
    if (shouldAnimateInitialOpen) ensureStyles();

    const rendered = ref(props.isOpen);
    const closing = ref(false);
    const sheetRef = ref<HTMLDivElement | null>(null);
    const animationState = ref<AnimationState>(
      shouldAnimateInitialOpen ? "enter" : "idle",
    );
    const activeAnimation = ref<FullScreenSheetAnimation>(
      props.animation || DEFAULT_ANIMATION,
    );
    let sheetId: string | null = null;
    let animationTimeout: number | null = null;
    let removeAnimationListener: (() => void) | null = null;

    const registerSheet = () => {
      if (sheetId || !navStack) return;
      sheetId = navStack.pushSheet(() => {
        void requestClose();
      });
    };

    const unregisterSheet = () => {
      if (sheetId && navStack) {
        navStack.popSheet(sheetId);
        sheetId = null;
      }
    };

    const animationType = () => props.animation || DEFAULT_ANIMATION;

    const clearAnimationWait = () => {
      if (animationTimeout != null) {
        window.clearTimeout(animationTimeout);
        animationTimeout = null;
      }
      if (removeAnimationListener) {
        removeAnimationListener();
        removeAnimationListener = null;
      }
    };

    const setRootAnimationState = (state: RootAnimationState) => {
      const appRoot = getAppRoot();
      if (!appRoot) return;

      appRoot.classList.remove(...ROOT_ANIMATION_CLASSES);
      if (!state) {
        delete appRoot.dataset.fssAnimation;
        return;
      }

      appRoot.dataset.fssAnimation = activeAnimation.value;
      appRoot.classList.add(`lmn-fss-root--${state}`);
    };

    const waitForSheetAnimation = (duration: number, onDone: () => void) => {
      const sheet = sheetRef.value;
      if (!sheet) {
        onDone();
        return;
      }

      let didFinish = false;
      const finish = () => {
        if (didFinish) return;
        didFinish = true;
        clearAnimationWait();
        onDone();
      };

      const onAnimationEnd = (event: AnimationEvent) => {
        if (event.target === sheet) finish();
      };

      sheet.addEventListener("animationend", onAnimationEnd);
      removeAnimationListener = () =>
        sheet.removeEventListener("animationend", onAnimationEnd);
      animationTimeout = window.setTimeout(finish, duration + 80);
    };

    const playEnter = () => {
      activeAnimation.value = animationType();
      clearAnimationWait();

      if (!shouldAnimateOnMobile()) {
        animationState.value = "idle";
        setRootAnimationState(null);
        return;
      }

      ensureStyles();
      animationState.value = "enter";
      nextTick(() => {
        setRootAnimationState("enter");
        waitForSheetAnimation(ENTER_DURATION_MS, () => {
          if (animationState.value !== "enter") return;
          animationState.value = "idle";
          setRootAnimationState("open");
        });
      });
    };

    const playExit = (onDone: () => void) => {
      activeAnimation.value = animationType();
      clearAnimationWait();

      if (!shouldAnimateOnMobile()) {
        animationState.value = "idle";
        setRootAnimationState(null);
        onDone();
        return;
      }

      ensureStyles();
      animationState.value = "exit";
      nextTick(() => {
        setRootAnimationState("exit");
        waitForSheetAnimation(EXIT_DURATION_MS, () => {
          animationState.value = "idle";
          setRootAnimationState(null);
          onDone();
        });
      });
    };

    // `force` skips the canClose veto — used by the isOpen watcher, where the
    // parent has already decided the sheet is closing.
    const requestClose = async (force = false) => {
      if (closing.value || !rendered.value) return;
      if (!force && props.canClose) {
        // Marked closing across the await so a second tap (or the back gesture
        // firing while the guard is still resolving) can't start a parallel
        // close; cleared again if the guard vetoes.
        closing.value = true;
        let allowed = false;
        try {
          allowed = await props.canClose();
        } catch {
          allowed = false;
        }
        if (!allowed) {
          closing.value = false;
          return;
        }
        closing.value = false;
      }
      closing.value = true;
      unregisterSheet();
      playExit(() => {
        rendered.value = false;
        closing.value = false;
        props.onClose?.();
      });
    };

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (isOpen) {
          closing.value = false;
          registerSheet();
          if (rendered.value) {
            nextTick(playEnter);
          } else {
            rendered.value = true;
          }
        } else if (rendered.value && !closing.value) {
          // Parent-driven close: it already decided, so don't run canClose.
          requestClose(true);
        }
      },
    );

    watch(rendered, (isRendered) => {
      if (isRendered) {
        nextTick(playEnter);
      }
    });

    onMounted(() => {
      if (props.isOpen) {
        registerSheet();
        nextTick(playEnter);
      }
    });

    onBeforeUnmount(() => {
      unregisterSheet();
      clearAnimationWait();
      setRootAnimationState(null);
    });

    return () => {
      if (!rendered.value) return null;

      return (
        <Teleport to="body">
          <div
            class={props.class}
            style={`position:fixed;inset:0;overflow:hidden;${props.style || ""}`}
          >
            <div
              ref={sheetRef}
              class="lmn-fullscreen-sheet__panel flex relative flex-col"
              data-fss-animation={activeAnimation.value}
              data-fss-state={animationState.value}
              style="height:100%;width:100%"
            >
              {slots.default?.({
                // Wrapped, not passed by reference: `close` is often bound
                // straight to onClick, and the click Event would land in
                // `force` and skip the canClose guard.
                close: () => {
                  void requestClose();
                },
              } satisfies FullScreenSheetSlotProps)}
            </div>
          </div>
        </Teleport>
      );
    };
  },
});
